<template>
  <div>
    <div v-if="!isCodeActivated">
      <BForm @submit.prevent="onSubmit">
        <BFormGroup :label="t('Invitation code')" label-for="invitationCodeActivation">
          <InvitationCodeField
            id="invitationCodeActivation"
            ref="codeField"
            v-model="invitationCode"
            :button-variant="checkButtonVariant"
            :input-variant="inputVariant"
            :external-error="codeActivationErrorMessage"
            @checked="onCodeChecked"
          />
        </BFormGroup>
        <div class="d-grid gap-2 mt-2">
          <BButton type="submit" variant="dark" :disabled="submitInProgress">
            <BSpinner v-if="submitInProgress" small class="me-1" />
            {{ t(buttonText) }}
          </BButton>
        </div>
      </BForm>
      </div>

      <div v-else>
        <p >{{ t('Invitation code activated.') }}</p>
        <p v-if="activationResponse?.vip_category">
          <i18n-t keypath="You have been assigned the {status} status." tag="span">
            <template #status>
              <b>"{{ activationResponse.vip_category.name }}"</b>
            </template>
          </i18n-t>
        </p>
        <div v-if="activationResponse?.vip_category?.benefits && activationResponse?.vip_category?.benefits.length > 0">
          <p>{{ t('Your benefits') }}:</p>
          <ul>
            <li v-for="benefit in activationResponse?.vip_category?.benefits" :key="benefit">
              {{ benefit }}
            </li>
          </ul>
        </div>
      </div>
      <div v-if="redirectUrl">
        <BButton :href="redirectUrl ?? undefined" variant="dark" class="w-100">
          {{ t('Continue') }}
        </BButton>
      </div>
      
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import InvitationCodeField from '~/components/invitation_code/InvitationCodeField.vue'
import { loginPathForSegments } from '~/shared/utils/routeMiddlewareHelpers'

const { getNormalizedUrl, isLocalUrl } = useUrlUtils()

const props = withDefaults(
  defineProps<{
    redirectToReg?: boolean
    redirectToRegPath?: string,
    redirectAfterActivation?: string,
    buttonText?: string,
    invitationCode?: string,
    autoActivate?: boolean,
    checkButtonVariant?: string,
    inputVariant?: string,
  }>(),
  {
    redirectToReg: false,
    redirectToRegPath: '/accounts/signup/',
    buttonText: 'Sign up',
    invitationCode: '',
    autoActivate: false,
    checkButtonVariant: 'light',
    inputVariant: 'light',
  },
)

interface VipCategory {
  id: BigInteger,
  name: string, 
  position: BigInteger,
  benefits: string[] | null,
}

interface ActivationResponse {
  code: string
  vip_category: VipCategory | null,
  redirect_after_activation: string | null
}

interface ActivationErrorResponse {
  message: string
  status?: number
}

const activationResponse = ref<ActivationResponse | null>(null)
const isCodeActivated = computed(() => activationResponse.value !== null)
const codeActivationErrorMessage = ref<string | null>(null)

const emit = defineEmits(['codeChecked', 'codeActivated'])

const { locale, t } = useI18n({ useScope: 'local' })
const localePath = useLocalePath()
const router = useRouter()
const route = useRoute()
const { $apiFetch } = useNuxtApp()
const { getSession } = useAuth()

const submitInProgress = ref(false)
const invitationCode = ref(props.invitationCode)
const codeField = ref<{
  check: () => Promise<boolean>
  isValid: boolean
  resetErrors: () => void
} | null>(null)

const redirectUrl = computed(() =>
  getNormalizedUrl(activationResponse.value?.redirect_after_activation),
)

watch(invitationCode, () => {
  codeActivationErrorMessage.value = null
})

const onCodeChecked = (valid: boolean) => {
  codeActivationErrorMessage.value = null
  emit('codeChecked', valid)
}

/**
 * Where to send someone whose session turned out to be dead: the login page of
 * this section, and back here afterwards with the code still in the URL.
 */
const loginUrlWithReturn = (code: string) => {
  const loginPath = localePath(loginPathForSegments(route.path.split('/').filter(Boolean)))
  const returnUrl = router.resolve({
    path: route.path,
    query: { ...route.query, invitation_code: code },
  }).fullPath
  return `${loginPath}?next=${encodeURIComponent(returnUrl)}`
}

const onSubmit = async () => {
  codeField.value?.resetErrors()
  const code = invitationCode.value.trim()
  if (!code) return

  codeActivationErrorMessage.value = null
  submitInProgress.value = true
  try {
    // Sign-up flow: the code is carried to the registration form and activated
    // there, as part of creating the account. Activating it here too would fire
    // an anonymous request the backend has no user to apply.
    if (props.redirectToReg) {
      await router.push({
        path: localePath(props.redirectToRegPath),
        query: { invitation_code: code },
      })
      return
    }

      activationResponse.value = await $apiFetch<ActivationResponse>(
        `/${locale.value}/api/invitations/invitations/activate/`,
        {
          method: 'POST',
          body: { code },
          // The VIP category is granted to the signed-in account -- without a
          // session there is nobody to grant it to, and the code stays unused.
          requireAuth: true,
        },
      )
      
      emit('codeActivated', activationResponse.value)
      getSession()

      if(activationResponse.value?.redirect_after_activation) {
        if(isLocalUrl(activationResponse.value.redirect_after_activation)) {
          await navigateTo(getNormalizedUrl(activationResponse.value.redirect_after_activation))
        } else {
          await router.push({
            path: activationResponse.value.redirect_after_activation,
          })
        }
      }
      if (props.redirectAfterActivation) {
        await navigateTo(getNormalizedUrl(props.redirectAfterActivation))
      }

    } catch (e) {
    const error = e as ActivationErrorResponse
    // Session gone between opening the page and pressing the button. The code
    // was never spent, so signing in and returning here activates it.
    if (error.status === 401) {
      codeActivationErrorMessage.value = t('Session expired. Please sign in and try again.')
      await navigateTo(loginUrlWithReturn(code))
      return
    }
    codeActivationErrorMessage.value = error.message ?? null
  } finally {
    submitInProgress.value = false
  }
}

const resetForm = () => {
  invitationCode.value = props.invitationCode
  codeActivationErrorMessage.value = null
  submitInProgress.value = false
}

watch(
  () => props.invitationCode,
  async (value) => {
    if (value) {
      invitationCode.value = value
      if (props.autoActivate && !submitInProgress.value && !isCodeActivated.value) {
        await onSubmit()
      }
    }
  },
)

onMounted(async () => {
  resetForm()

  if (!props.autoActivate) return

  if (!invitationCode.value.trim()) {
    console.warn('InvitationCodeActivation: autoActivate is true but invitationCode is empty')
    return
  }

  await onSubmit()
})
</script>

<i18n lang="json">
{
  "ru": {
    "Invitation code": "Код приглашения",
    "Sign up": "Зарегистрироваться",
    "Invitation code activated.": "Код приглашения активирован.",
    "You have been assigned the {status} status.": "Вам присвоен статус {status}.",
    "Your benefits": "Ваши привилегии",
    "Continue": "Продолжить",
    "Session expired. Please sign in and try again.": "Сессия истекла. Войдите в аккаунт и повторите — код не был использован."
  }
}
</i18n>
